// The signed-in user and the session's lifecycle (P1-E05). SessionGate reads /admin/auth/me (the client
// refreshes silently first when the access token is missing or expired), sends a signed-out visitor to
// /login and an Admin who has not passed MFA to /login/mfa, opens the realtime stream, and signs out.
// Roles and scopes are UI hints only: the server enforces RBAC (CODE_STRUCTURE §3).
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { onAuthProblem } from '@/api/auth';
import { ApiError } from '@/api/client';
import { startJobsStream } from '@/api/jobs';
import { meQuery, type Me } from '@/api/queries';
import { signOut } from '@/features/auth/api';
import i18n, { rememberLocale } from '@/i18n';
import { paths } from './paths';

export type Session = {
  me: Me;
  isAdmin: boolean;
  /** Whether the user holds a scope (`me.scopes`): UI hints only, the server enforces them. */
  can: (scope: string) => boolean;
  logout: () => void;
};
const SessionContext = createContext<Session | null>(null);

/** Provides a session directly (SessionGate uses it; tests use it with a fixed user). */
export function SessionProvider({ value, children }: { value: Session; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** An Admin must have passed MFA in this session (TZ §3); until then the API allows only /me and MFA. */
export function needsMfa(me: Me): boolean {
  return me.role === 'admin' && !me.mfa_verified;
}

export function SessionGate({ children }: { children: ReactNode }) {
  const { data: me, error, isPending } = useQuery(meQuery);
  const qc = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  // The server copy of the locale wins on sign-in (users.locale).
  useEffect(() => {
    const locale = me?.locale === 'uz' ? 'uz' : 'ru';
    if (me && locale !== i18n.language) {
      rememberLocale(locale);
      void i18n.changeLanguage(locale);
    }
  }, [me]);

  // A refresh that failed mid-session, or an Admin route answered with 403 mfa_required.
  useEffect(
    () =>
      onAuthProblem((p) => {
        if (p === 'signed_out') {
          qc.clear();
          navigate(paths.login(), { replace: true, state: { from: location.pathname } });
        } else navigate(paths.mfa(), { replace: true });
      }),
    [qc, navigate, location.pathname],
  );

  const ready = !!me && !needsMfa(me);
  useEffect(() => (ready ? startJobsStream() : undefined), [ready]);

  const session = useMemo<Session | null>(
    () =>
      me
        ? {
            me,
            isAdmin: me.role === 'admin',
            can: (scope) => me.scopes.includes(scope),
            logout: () => {
              void signOut().finally(() => {
                qc.clear();
                navigate(paths.login(), { replace: true });
              });
            },
          }
        : null,
    [me, qc, navigate],
  );

  if (isPending) return null;
  if (error) {
    if (error instanceof ApiError && error.status === 401) {
      return <Navigate to={paths.login()} replace state={{ from: location.pathname }} />;
    }
    throw error;
  }
  if (!session || needsMfa(session.me)) return <Navigate to={paths.mfa()} replace state={{ from: location.pathname }} />;
  return <SessionProvider value={session}>{children}</SessionProvider>;
}

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession must be used inside <SessionGate>');
  return s;
}
